import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, FileText, CalendarDays, Image, Settings, ChevronLeft, Megaphone, Plug, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';

const mainNav = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Companies', icon: Building2, path: '/companies' },
  { label: 'Integrations', icon: Plug, path: '/integrations' },
];

const companyNav = [
  { label: 'Overview', icon: Building2, path: '' },
  { label: 'Accounts', icon: Users, path: '/accounts' },
  { label: 'Posts', icon: FileText, path: '/posts' },
  { label: 'Calendar', icon: CalendarDays, path: '/calendar' },
  { label: 'Media', icon: Image, path: '/media' },
  { label: 'AI Studio', icon: Sparkles, path: '/ai' },
  { label: 'Settings', icon: Settings, path: '/settings' },
];

export function Sidebar({ open, onClose, companyName }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId } = useParams();

  const isCompanyPage = location.pathname.startsWith('/companies/') && companyId;

  const handleNavigate = (path) => {
    navigate(path);
    onClose?.();
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/companies') return location.pathname === '/companies' || (location.pathname.startsWith('/companies') && !companyId);
    if (path === '/integrations') return location.pathname === '/integrations';
    return false;
  };

  const isCompanySubActive = (subPath) => {
    if (!companyId) return false;
    const fullPath = `/companies/${companyId}${subPath}`;
    if (subPath === '') {
      return location.pathname === `/companies/${companyId}`;
    }
    return location.pathname.startsWith(fullPath);
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-[272px] bg-card border-r flex flex-col transition-transform duration-200 lg:translate-x-0 lg:static lg:z-auto',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="h-14 flex items-center px-5 border-b shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Megaphone className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-display font-semibold text-lg tracking-tight">SocialHub</span>
          </div>
        </div>

        <ScrollArea className="flex-1 py-3">
          {/* Main navigation */}
          <nav className="px-3 space-y-1">
            {mainNav.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150',
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </nav>

          {/* Company sub-navigation */}
          {isCompanyPage && (
            <>
              <Separator className="my-3" />
              <div className="px-3 mb-2">
                <button
                  onClick={() => handleNavigate('/companies')}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Back to Companies
                </button>
                <p className="text-sm font-semibold mt-2 truncate" title={companyName}>
                  {companyName || 'Company'}
                </p>
              </div>
              <nav className="px-3 space-y-1">
                {companyNav.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(`/companies/${companyId}${item.path}`)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150',
                      isCompanySubActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                    data-testid={`company-nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </nav>
            </>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-3 border-t shrink-0">
          <p className="text-xs text-muted-foreground text-center">SocialHub v1.0</p>
        </div>
      </aside>
    </>
  );
}
